import type { CityConfig } from "../city-template/types";

export const rochesterMNConfig: CityConfig = {
  city: "Rochester",
  state: "Minnesota",
  stateAbbr: "MN",
  slug: "rochester-mn",
  route: "/cities/rochester-mn",
  title: "Emergency Passport Rochester, MN — Expedited & Fast Passport Service | Fast Passport Center",
  metaDescription: "Emergency passport and expedited passport services in Rochester, MN — as fast as 24 hours. Fast passport renewal, same-day help, and child passport expediting for Olmsted County and Mayo Clinic travelers. A+ BBB rated, registered State Dept. courier.",
  h1: "Emergency & Expedited Passport Services in Rochester, MN",
  heroSubtitle: "Passport expired before your trip? Need an emergency passport fast? We help Rochester and Olmsted County travelers get their expedited passports as quickly as 24 hours. A+ BBB rated. No office visit required.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Rochester%20Minnesota%20cityscape%20at%20golden%20hour%20with%20modern%20downtown%20skyline%2C%20Mayo%20Clinic%20Gonda%20Building%20and%20Plummer%20Building%20iconic%20architecture%2C%20Zumbro%20River%20flowing%20through%20the%20city%2C%20warm%20amber%20sunset%20light%20reflecting%20on%20medical%20campus%20buildings%2C%20clean%20professional%20cityscape%20photography%20with%20warm%20earth%20tones%2C%20no%20readable%20text%20or%20people%2C%20crisp%20and%20inviting%20atmosphere&width=1800&height=700&seq=rochester-mn-hero-001&orientation=landscape",
  heroImageAlt: "Rochester Minnesota skyline with Mayo Clinic campus at golden hour",
  geoPosition: "44.0121;-92.4802",
  geoPlacename: "Rochester, Minnesota",
  lastModified: "2026-06-14",
  regionalAgency: {
    name: "Minneapolis Passport Agency",
    address: "212 3rd Ave S, Minneapolis, MN 55401",
    distance: "Approximately 85 miles from Rochester — about 1.5 hours via US-52",
  },
  nearbyCitiesZips: [
    { name: "Downtown Rochester", zips: "55901, 55902" },
    { name: "Northwest Rochester", zips: "55901" },
    { name: "Southeast Rochester", zips: "55904" },
    { name: "Stewartville", zips: "55976" },
    { name: "Byron", zips: "55920" },
    { name: "Kasson", zips: "55944" },
    { name: "Oronoco", zips: "55960" },
    { name: "Pine Island", zips: "55963" },
    { name: "Chatfield", zips: "55923" },
    { name: "St. Charles", zips: "55972" },
    { name: "Plainview", zips: "55964" },
    { name: "Eyota", zips: "55934" },
  ],
  benefits: [
    { icon: "ri-time-line", title: "24–48 Hour Processing", desc: "Emergency courier submission for urgent Rochester departures" },
    { icon: "ri-shield-check-line", title: "Registered Courier", desc: "U.S. Department of State approved passport expediting" },
    { icon: "ri-user-line", title: "Dedicated Agent", desc: "A real person assigned to your case from start to finish" },
    { icon: "ri-mail-send-line", title: "Document Shipping", desc: "Prepaid overnight labels to our processing center" },
  ],
  internalLinks: [
    { name: "Fast passport renewal in Minneapolis", route: "/cities/minneapolis" },
    { name: "Emergency passport help in St. Paul", route: "/cities/st-paul" },
    { name: "Urgent passport services in Duluth", route: "/cities/duluth-mn" },
    { name: "Expedited passport services in Chicago", route: "/states/illinois" },
    { name: "Passport courier services in Iowa", route: "/states/iowa" },
    { name: "Rush passport help in Wisconsin", route: "/states/illinois" },
  ],
  fpcOfficeAddress: "230 S Dearborn St Suite 380, Chicago, IL 60604",
  fpcOfficeNote: "Fast Passport Center serves Rochester remotely via secure courier. We do not have a walk-in office in Rochester — our registered couriers hand-deliver your documents directly to U.S. Department of State processing facilities. This is how we achieve 24–48 hour turnaround for Rochester travelers.",
  fpcOfficeDistance: "Serving all of Olmsted County and southeastern Minnesota",
  serviceAreaIntro: "Fast Passport Center provides emergency and fast passport renewal services throughout the entire Rochester area. From downtown Rochester to the surrounding Olmsted County communities, from Mayo Clinic professionals to IBM employees — we help southeastern Minnesota travelers get their passports fast, often in 24–48 hours through our registered courier service.",
  nearbyCitiesIntro: "We serve all Rochester neighborhoods and nearby communities including Stewartville, Byron, Kasson, Oronoco, Pine Island, Chatfield, and St. Charles. If you are in Olmsted County or southeastern Minnesota, we have you covered.",
  howItWorksIntro: "Most Rochester travelers complete everything online and ship documents overnight. No need to drive to Chicago or wait in line. Our emergency and expedited passport process is designed for southeastern Minnesota travelers who need fast, reliable results.",
  mistakesIntro: "With Rochester International Airport (RST) and Mayo Clinic's global reach, Rochester travelers can't afford passport delays. Here are the most common mistakes we see.",
  testimonialsIntro: "Rochester travelers trust us when their departure is days away. Here is what some of them have to say.",
  faqIntro: "Have questions about expedited passports in Rochester? Here are the most common ones we hear from Olmsted County travelers.",
  statePageRoute: "/states/minnesota",
  statePageName: "Minnesota",
  dmvWebsite: "https://dps.mn.gov/divisions/dvs",
  dmvLabel: "Minnesota DVS — Official Website",
  stickyNavLinks: [
    { label: "Processing", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Comparison", href: "#comparison" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  processingOptions: [
    {
      speed: "2–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Predictable turnaround",
        "Ideal for upcoming trips",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Faster than self-service",
        "Good for planned travel",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–2",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: [
        "No courier access — standard mail only",
        "Workload & wait times shift with shutdowns and budget fights",
        "In-person clerk appointment required",
        "No document review — errors cause rejection",
        "No dedicated agent — you are on your own",
        "Updates only by physical mail letter",
        "No phone or email contact for your case",
        "Can't upgrade speed after submission",
        "Not suitable if travel is within 10 weeks",
      ],
      highlight: false,
      muted: true,
    },
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: [
        "Appointment required — no walk-ins allowed",
        "Life-or-death emergencies only",
        "Bereavement or medical crisis",
        "Expect to wait all day or return next day",
      ],
      highlight: false,
      emergency: true,
    },
  ],
  services: [
    {
      icon: "ri-flashlight-line",
      title: "Emergency Passport",
      description: "For travelers with immediate departure needs — as fast as 24 hours for urgent Rochester international flights.",
      bullets: ["24–48 hour turnaround available", "Registered courier hand-delivery", "Flight verification support"],
      badge: "Most Popular",
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-passport-line",
      title: "Passport Renewal",
      description: "Fast renewal for adults with a passport issued within the last 15 years at age 16 or older.",
      bullets: ["No clerk visit required for most", "Mail-in or courier options", "Lost passport replacement available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-add-circle-line",
      title: "New Passport",
      description: "First-time passport application guidance with acceptance clerk appointment support.",
      bullets: ["Acceptance facility locator", "Form DS-11 assistance", "Photo requirement guidance"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-alert-line",
      title: "Lost or Stolen Passport",
      description: "Passport replacement with DS-64 form guidance and police report documentation.",
      bullets: ["DS-64 form assistance", "Identity verification support", "Emergency replacement options"],
      badge: "Urgent",
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Passport services for minors under 16 with parental consent and acceptance clerk support.",
      bullets: ["DS-11 form for minors", "Parental consent guidance", "Both parents presence rules"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-edit-line",
      title: "Name Change",
      description: "Passport correction and name change service after marriage, divorce, or legal changes.",
      bullets: ["DS-5504 or DS-82 guidance", "Marriage certificate support", "Court order documentation"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport", description: "Urgent passport processing for travelers with immediate departure needs, as fast as 24 hours." },
    { name: "Passport Renewal", description: "Fast passport renewal for U.S. citizens with a passport issued within the last 15 years at age 16 or older." },
    { name: "New Passport", description: "First-time passport application assistance with step-by-step guidance through the acceptance clerk process." },
    { name: "Lost or Stolen Passport", description: "Passport replacement service for lost or stolen passports, including DS-64 form guidance." },
    { name: "Child Passport", description: "Passport services for minors under 16, with parental consent guidance and acceptance clerk support." },
    { name: "Name Change", description: "Passport correction and name change service after marriage, divorce, or legal name changes." },
  ],
  testimonials: [
    {
      name: "Dr. Sarah K.",
      location: "Rochester, MN",
      rating: 5,
      text: "Mayo Clinic sent me to an international conference in Geneva with 10 days notice and my passport had expired. Fast Passport Center got me a new one in 48 hours. Absolutely saved my presentation.",
      service: "Emergency Passport",
    },
    {
      name: "James T.",
      location: "Stewartville, MN",
      rating: 5,
      text: "I needed to renew my passport for a family vacation to Norway. The process was smooth, they reviewed my documents, and I had my passport in 5 days. Highly recommend.",
      service: "Passport Renewal",
    },
    {
      name: "Maria L.",
      location: "Rochester, MN",
      rating: 5,
      text: "Lost my passport the week before visiting family in Mexico. They walked me through the DS-64 process and got me a replacement in 2 days. Incredible service.",
      service: "Lost Passport",
    },
    {
      name: "Dr. Robert P.",
      location: "Rochester, MN",
      rating: 5,
      text: "As a Mayo Clinic physician who travels internationally for research, I can't afford passport delays. Fast Passport Center has been my go-to for years. Professional, fast, and reliable every time.",
      service: "Expedited Renewal",
    },
  ],
  facilities: [
    {
      id: "rst-1",
      name: "Rochester Main Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "200 1st Ave SW",
      city: "Rochester",
      state: "MN",
      zip: "55902",
      phone: "(507) 287-1222",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.5 miles from downtown Rochester",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Acceptance hours are limited. Appointments required. Call ahead to confirm passport acceptance times.",
      icon: "ri-mail-line",
    },
    {
      id: "rst-2",
      name: "Rochester Northwest Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "4221 55th St NW",
      city: "Rochester",
      state: "MN",
      zip: "55901",
      phone: "(507) 287-1223",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "4 miles from downtown Rochester",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for northwest Rochester and IBM area residents. Limited weekend hours.",
      icon: "ri-mail-line",
    },
    {
      id: "rst-3",
      name: "Stewartville Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "110 Main St N",
      city: "Stewartville",
      state: "MN",
      zip: "55976",
      phone: "(507) 533-4401",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "12 miles from downtown Rochester",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Popular for southern Olmsted County residents. Appointment strongly recommended.",
      icon: "ri-mail-line",
    },
    {
      id: "rst-4",
      name: "Byron City Clerk",
      type: "City Clerk Passport Acceptance",
      address: "111 5th St NW",
      city: "Byron",
      state: "MN",
      zip: "55920",
      phone: "(507) 775-3400",
      hours: "Mon–Fri: 8:00 AM – 4:30 PM",
      distance: "8 miles from downtown Rochester",
      website: "https://www.byronmn.gov/",
      websiteLabel: "Byron City Clerk",
      notes: "Accepts walk-ins but appointments preferred. Photo services available on-site.",
      icon: "ri-government-line",
    },
    {
      id: "rst-5",
      name: "Kasson Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "101 2nd Ave NW",
      city: "Kasson",
      state: "MN",
      zip: "55944",
      phone: "(507) 634-4461",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "15 miles from downtown Rochester",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for Dodge County residents. Limited passport hours — call to confirm.",
      icon: "ri-mail-line",
    },
    {
      id: "rst-6",
      name: "Olmsted County Service Center",
      type: "County Clerk Passport Acceptance",
      address: "151 4th St SE",
      city: "Rochester",
      state: "MN",
      zip: "55904",
      phone: "(507) 328-6000",
      hours: "Mon–Fri: 8:00 AM – 4:30 PM",
      distance: "0.3 miles from downtown Rochester",
      website: "https://www.olmstedcounty.gov/",
      websiteLabel: "Olmsted County",
      notes: "Major county facility. Accepts walk-ins but appointments recommended for faster service.",
      icon: "ri-government-line",
    },
  ],
  comparisonRows: [
    { feature: "Appointment Required", agency: "Yes — call NPIC, limited slots in Minneapolis", fpc: "No appointment needed to start" },
    { feature: "In-Person Visit", agency: "Required — 85 mile drive to Minneapolis via US-52", fpc: "Most Rochester travelers ship documents overnight" },
    { feature: "Processing Speed", agency: "Life-or-death emergencies only — strict eligibility", fpc: "As fast as 24 hours — multiple speed options" },
    { feature: "Document Review", agency: "None — errors cause rejection and delay", fpc: "Professional review to reduce processing delays" },
    { feature: "Courier Access", agency: "Not available to the public", fpc: "Reserved daily submission availability as registered courier" },
    { feature: "Predictability", agency: "Appointments can be cancelled — no guarantee", fpc: "Predictable processing based on selected speed option" },
    { feature: "Who Can Use It", agency: "Strict eligibility — urgent travel only", fpc: "Most U.S. citizens with required documentation" },
    { feature: "Cost vs Resellers", agency: "Government fees only — but you handle logistics", fpc: "Best price guarantee — 30-100% less than FedEx, Staples & resellers" },
    { feature: "Rochester-Specific Guidance", agency: "Generic — no Mayo Clinic, IBM, or RST expertise", fpc: "Full Mayo/IBM/Olmsted County/Minneapolis courier knowledge built in" },
  ],
  mistakes: [
    {
      icon: "ri-image-line",
      title: "Wrong Photo Size or Background",
      description: "Rochester photo shops often use generic sizes. Passport photos must be exactly 2×2 inches with a white or off-white background.",
    },
    {
      icon: "ri-file-list-line",
      title: "Incomplete DS-11 Form",
      description: "Missing signatures or using the wrong form type are the #1 reasons for rejection at Rochester acceptance facilities.",
    },
    {
      icon: "ri-money-dollar-circle-line",
      title: "Wrong Payment Amount",
      description: "Fees change frequently. Many Rochester applicants show up with incorrect checks and have to reschedule their appointment.",
    },
    {
      icon: "ri-calendar-close-line",
      title: "No Proof of Travel",
      description: "Without flight confirmation or travel itinerary, the regional agency will turn you away for emergency appointments.",
    },
    {
      icon: "ri-parent-line",
      title: "Missing Parental Consent",
      description: "For minors, both parents must appear or submit notarized consent. This trips up many Rochester families at the clerk.",
    },
    {
      icon: "ri-time-line",
      title: "Waiting Too Long to Start",
      description: "Rochester travelers often assume standard processing will be fine. It takes 8–11 weeks. Start expedited at least 3 weeks before travel.",
    },
  ],
  steps: [
    { number: "1", icon: "ri-file-list-line", title: "Apply Online", description: "Complete your application on our secure website in under 10 minutes." },
    { number: "2", icon: "ri-mail-send-line", title: "Ship Documents", description: "We send you a prepaid overnight label. Drop your documents at any UPS location in Rochester." },
    { number: "3", icon: "ri-shield-check-line", title: "We Review", description: "Our team checks every document for accuracy before hand-delivering to the State Department." },
    { number: "4", icon: "ri-passport-line", title: "Processing", description: "Your passport is processed at a U.S. Department of State facility using our registered courier slots." },
    { number: "5", icon: "ri-truck-line", title: "Delivery", description: "We overnight your new passport back to your Rochester address — trackable every step of the way." },
  ],
  faqs: [
    {
      q: "How fast can I get an emergency passport in Rochester?",
      a: "Fast Passport Center offers emergency passport processing in Rochester in as little as 24–48 hours. Our registered couriers hand-deliver your documents directly to U.S. Department of State facilities, bypassing standard mail delays. This is the fastest reliable option for Olmsted County travelers with urgent international flights.",
    },
    {
      q: "Can I get a same-day passport in Rochester?",
      a: "Same-day passports are only available at regional passport agencies for documented life-or-death emergencies with proof of travel within 72 hours. The nearest agency to Rochester is the Minneapolis Passport Agency at 212 3rd Ave S — about 1.5 hours via US-52. For all other urgent travel, Fast Passport Center's 24–48 hour emergency courier processing is the fastest guaranteed option available to Rochester residents.",
    },
    {
      q: "Where can I get a fast passport renewal in Rochester?",
      a: "Fast Passport Center offers fast passport renewal in Rochester with no office visit required. We serve all Olmsted County neighborhoods including downtown, northwest Rochester, and the IBM corridor. Simply complete your application online, ship your documents using our prepaid overnight label, and our couriers handle the rest.",
    },
    {
      q: "What is the fastest way to get an urgent passport in Rochester?",
      a: "The fastest way to get an urgent passport in Rochester is through Fast Passport Center's emergency courier service. Our registered couriers deliver your documents directly to State Department processing facilities, achieving 24–48 hour turnaround. This is significantly faster than standard mail processing (8–11 weeks) or even government expedited mail (2–3 weeks).",
    },
    {
      q: "How fast can I get a passport in Rochester?",
      a: "With Fast Passport Center's emergency courier service, Rochester travelers can receive their passport in as little as 24–48 hours. Our registered couriers hand-deliver your documents directly to U.S. Department of State processing facilities, bypassing standard mail delays.",
    },
    {
      q: "Do I need to visit a passport agency in person?",
      a: "No. With Fast Passport Center, you complete everything online and ship your documents overnight. Our couriers handle the in-person delivery to State Department facilities. You only need to visit a local acceptance clerk if you're a first-time applicant or need a name change.",
    },
    {
      q: "Where are the closest passport acceptance facilities in Rochester?",
      a: "Popular options include the Rochester Main Post Office (55902), Rochester Northwest Post Office (55901), and Olmsted County Service Center (55904). All require appointments. We help you find the most convenient location and schedule your visit.",
    },
    {
      q: "What if I work at Mayo Clinic and need an urgent passport?",
      a: "Mayo Clinic professionals frequently travel internationally for conferences, research collaborations, and patient consultations. We specialize in expedited passport services for healthcare professionals on tight timelines — as fast as 24 hours through our emergency courier service.",
    },
    {
      q: "How much does expedited passport service cost in Rochester?",
      a: "Government fees are fixed: $130 for a passport book (adult) plus a $60 expedite fee. Fast Passport Center's service fees vary by processing speed — emergency 24–48 hour processing, 3–5 day expedited, or standard options. Contact us for a personalized quote based on your timeline.",
    },
    {
      q: "Do you help with passport photos in Rochester?",
      a: "We guide you on exact photo requirements and can recommend locations near you. Many CVS, Walgreens, and FedEx locations in Rochester offer passport photo services. We review your photos before submission to ensure they meet State Department standards.",
    },
    {
      q: "What documents do I need for a first-time passport in Rochester?",
      a: "You'll need proof of citizenship (birth certificate or naturalization certificate), government-issued photo ID (driver's license or REAL ID), two passport photos, and payment. We provide a complete checklist and review your documents before you visit the acceptance clerk.",
    },
    {
      q: "Can you help with a child's passport in Rochester?",
      a: "Yes. Child passports require both parents (or notarized consent from the absent parent), the child's birth certificate, and both parents' IDs. We walk Rochester families through the entire process and help schedule acceptance clerk appointments.",
    },
  ],
  photoLocations: [
    { name: "CVS Pharmacy — Rochester", address: "1021 15th Ave SE, Rochester, MN 55904", hours: "Mon–Sun: 8AM–10PM" },
    { name: "Walgreens — Downtown Rochester", address: "1201 Broadway Ave S, Rochester, MN 55904", hours: "Mon–Sun: 7AM–11PM" },
    { name: "FedEx Office — Rochester", address: "3708 55th St NW, Rochester, MN 55901", hours: "Mon–Fri: 7AM–9PM, Sat: 8AM–6PM" },
    { name: "UPS Store — Rochester", address: "1201 S Broadway, Rochester, MN 55904", hours: "Mon–Fri: 8AM–7PM, Sat: 9AM–5PM" },
    { name: "Walmart — Rochester", address: "3400 55th St NW, Rochester, MN 55901", hours: "Mon–Sun: 6AM–11PM" },
    { name: "Rite Aid — Rochester", address: "1200 7th St NW, Rochester, MN 55901", hours: "Mon–Sun: 8AM–10PM" },
    { name: "Walgreens — Stewartville", address: "100 Main St N, Stewartville, MN 55976", hours: "Mon–Sun: 7AM–11PM" },
    { name: "CVS Pharmacy — Kasson", address: "101 2nd Ave NW, Kasson, MN 55944", hours: "Mon–Sun: 8AM–10PM" },
  ],
  dmvLocations: [
    {
      name: "DMV — Rochester",
      address: "2116 Campus Dr SE, Rochester, MN 55904",
      hours: "Mon–Fri: 8AM–5PM",
      note: "REAL ID and driver's license renewals. Appointment required.",
      featured: true,
    },
    {
      name: "DMV — Rochester North",
      address: "4221 55th St NW, Rochester, MN 55901",
      hours: "Mon–Fri: 8AM–5PM",
      note: "Passport-ready photo services available. Online appointment booking.",
      featured: false,
    },
    {
      name: "DMV — Kasson",
      address: "101 2nd Ave NW, Kasson, MN 55944",
      hours: "Mon–Fri: 8AM–5PM",
      note: "REAL ID processing. Walk-ins limited — book online first.",
      featured: false,
    },
    {
      name: "DMV — Stewartville",
      address: "100 Main St N, Stewartville, MN 55976",
      hours: "Mon–Fri: 8AM–5PM",
      note: "Full-service center for southern Olmsted County residents.",
      featured: true,
    },
  ],
  airports: [
    {
      code: "RST",
      name: "Rochester International Airport",
      distance: "8 miles from downtown Rochester",
      address: "7600 Helgerson Dr SW, Rochester, MN 55902",
      note: "The primary airport for southeastern Minnesota. Offers domestic flights with connections to international hubs.",
      flightUrl: "https://www.flyrst.com/",
      flightLabel: "RST Flight Info",
      featured: true,
    },
    {
      code: "MSP",
      name: "Minneapolis–Saint Paul International Airport",
      distance: "85 miles from Rochester",
      address: "4300 Glumack Dr, St. Paul, MN 55111",
      note: "Major international hub for southeastern Minnesota travelers connecting through MSP.",
      flightUrl: "https://www.mspairport.com/",
      flightLabel: "MSP Flight Info",
      featured: false,
    },
  ],
  vitalRecordsIcon: "ri-file-text-line",
  vitalRecordsLinks: [
    {
      icon: "ri-user-smile-line",
      label: "Birth Certificate",
      desc: "Request certified birth certificates from the Minnesota Department of Health Vital Records. Online, mail, or in-person options available. Olmsted County also provides birth records for Rochester residents.",
      url: "https://www.health.state.mn.us/people/vitalrecords/"
    },
    {
      icon: "ri-file-list-2-line",
      label: "Death Certificate",
      desc: "Obtain certified death certificates through the Minnesota Department of Health Vital Records. Required for estate, legal, and survivor benefit matters.",
      url: "https://www.health.state.mn.us/people/vitalrecords/"
    },
    {
      icon: "ri-hearts-line",
      label: "Marriage Certificate",
      desc: "Request certified marriage records from Olmsted County Vital Records. Required for name change passport applications. Serving Rochester and all Olmsted County communities.",
      url: "https://www.olmstedcounty.gov/residents/records-licenses"
    },
    {
      icon: "ri-file-copy-line",
      label: "Divorce Decree",
      desc: "Obtain certified divorce records through the Olmsted County District Court. Required for name change passport corrections and legal documentation.",
      url: "https://www.mncourts.gov/"
    }
  ],
};

export default rochesterMNConfig;